import React, { useState, useRef } from 'react';
import {
  useTranslator,
  // FormikInput as Input,
  useToast,
  callNativeAppRemoveLastRoute,
  useAppContext,
} from '@front-activerse/common-components';
import {
  Container,
  Row,
  Col,
  Button,
  Alert,
  Spinner,
  Form,
  InputGroupText,
  Input,
  Label,
} from 'reactstrap';
import Layout from '../../Layout/Layout';
import TitleBar from '../../Layout/TitleBar';
import { useNavigate, Link } from 'react-router-dom';
import { useGlobalIngredientsEdit } from '../hooks/useGlobalIngredientsEdit';
import { Ingredient } from '../types';

const MULTIPLIER_STORAGE_KEY = 'tracker_multiplier';

interface AnalyzeIngredientInput {
  description: string;
  baseAmount: number;
  baseUnit: string;
}

interface AnalyzeIngredientResponse {
  __typename: string;
  primaryIngredient: {
    productId: string;
    name: string;
    brand?: string;
    baseAmount: number;
    baseUnit: string;
    calories: number;
    proteinGrams: number;
    carbsGrams: number;
    fatGrams: number;
  };
  alternateIngredients: {
    productId: string;
    name: string;
    brand?: string;
    baseAmount: number;
    baseUnit: string;
    calories: number;
    proteinGrams: number;
    carbsGrams: number;
    fatGrams: number;
  }[];
}

interface TrackerAddIngredientContainerProps {
  analyzeIngredient: (input: AnalyzeIngredientInput) => Promise<AnalyzeIngredientResponse>;
  cancelAnalyzeIngredient: () => void;
}

const TrackerAddIngredientContainer: React.FC<TrackerAddIngredientContainerProps> = ({
  analyzeIngredient,
  cancelAnalyzeIngredient,
}) => {
  const { t, language } = useTranslator();
  const navigate = useNavigate();
  const { toastError, toastSuccess } = useToast();
  const { ingredients, updateIngredients } = useGlobalIngredientsEdit([]);
  const isCanceledRef = useRef(false);

  const [showNoFoodAlert, setShowNoFoodAlert] = useState(false);
  const [results, setResults] = useState<any>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [isAiSearching, setIsAiSearching] = useState(false);
  const [description, setDescription] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);

  const decimalSeparator = language === 'pl-PL' ? ',' : '.';

  const {
    images: { scanBarcodeIcon, plus, searchIcon, aiBadgeTiny, searchButton },
  } = useAppContext();

  // Get multiplier from sessionStorage (default to 1 if not found)
  const multiplier = parseFloat(sessionStorage.getItem(MULTIPLIER_STORAGE_KEY) || '1');

  // Get system of measures from localStorage
  const systemOfMeasures = localStorage.getItem('systemOfMeasuresNutrition') || 'si';

  const handleAddIngredient = (selectedIngredient: any) => {
    // User entered amounts for current number of portions
    // We need to divide by multiplier to store for 1 portion in sessionStorage
    const amountFor1Portion = selectedIngredient.baseAmount / multiplier;
    const caloriesFor1Portion = Math.round(selectedIngredient.calories / multiplier);
    const proteinFor1Portion =
      Math.round((selectedIngredient.proteinGrams / multiplier) * 100) / 100;
    const fatFor1Portion = Math.round((selectedIngredient.fatGrams / multiplier) * 100) / 100;
    const carbsFor1Portion = Math.round((selectedIngredient.carbsGrams / multiplier) * 100) / 100;
    // Transform API response to Ingredient type (stored for 1 portion)
    const newIngredient: Ingredient = {
      id: selectedIngredient.productId,
      name: selectedIngredient.name,
      brand: selectedIngredient.brand,
      kcal: caloriesFor1Portion,
      measurements: [
        {
          amount: amountFor1Portion,
          text: `${amountFor1Portion} ${selectedIngredient.baseUnit}`,
          unit: selectedIngredient.baseUnit,
        },
      ],
      macro: {
        protein: { grams: proteinFor1Portion },
        fat: { grams: fatFor1Portion },
        carbohydrates: { grams: carbsFor1Portion },
      },
    };
    // Add new ingredient to the list (will be stored in sessionStorage via useGlobalIngredientsEdit)
    updateIngredients([...ingredients, newIngredient]);
    navigate(-1);
    callNativeAppRemoveLastRoute();
    toastSuccess(<p>{t('tracker.meal.ingredients.addedConfirmation')}</p>);
  };

  const handleSubmit = async (lookupStrategy: string, event?: React.FormEvent<HTMLFormElement>) => {
    const chosenLookupStrategy = lookupStrategy || 'db_only';

    event?.preventDefault();
    if (description.trim() === '') {
      return;
    }
    searchInputRef.current?.blur();
    setShowNoFoodAlert(false);
    setIsSearching(true);
    setResults(null);

    // Prevent retry if user canceled the previous request
    if (isCanceledRef.current) {
      isCanceledRef.current = false;
      setIsSearching(false);
      setIsAiSearching(false);
      return;
    }

    const request = {
      description,
      baseAmount: systemOfMeasures === 'si' || systemOfMeasures === 'si_cl' ? 100 : 3,
      baseUnit: systemOfMeasures === 'si' || systemOfMeasures === 'si_cl' ? 'grams' : 'oz',
      lookupStrategy: chosenLookupStrategy,
    };

    try {
      const response = await analyzeIngredient(request);

      if (response.__typename === 'AnalyzedDailyTrackerIngredients') {
        const primaryIngredient = response.primaryIngredient;
        const alternateIngredients = response.alternateIngredients || [];

        setResults([primaryIngredient, ...alternateIngredients]);

        if (chosenLookupStrategy === 'llm_only') {
          handleAddIngredient(primaryIngredient);
          return;
        }
      } else if (response.__typename === 'NotFoodError') {
        setResults([]);
        setShowNoFoodAlert(true);
      } else if (response.__typename === 'ProductNotFoundError') {
        setResults([]);
      } else {
        toastError();
      }
    } catch (error) {
      // Don't show error if operation was canceled
      // Note: setSubmitting(false) is already called in handleCancel
      if (isCanceledRef.current || (error as Error)?.name === 'AbortError') {
        return;
      }
      // For other errors, reset submitting state and show error
      toastError();
    } finally {
      setIsSearching(false);
      setIsAiSearching(false);
    }
  };

  const handleSearchWithAI = async () => {
    if (description.trim() === '') {
      return;
    }
    setIsAiSearching(true);
    handleSubmit('llm_only');
  };

  const handleCancel = () => {
    isCanceledRef.current = true;
    cancelAnalyzeIngredient();
    setIsSearching(false);
    setIsAiSearching(false);
    setTimeout(() => {
      isCanceledRef.current = false;
    }, 0);
  };

  return (
    <Layout>
      <TitleBar showBackBtn={true}>{t('tracker.logMeal.addIngredient.navbarTitle')}</TitleBar>

      <div className="page-template-tracker edit-meal-page">
        <Container className="py-md-4 full-height">
          <Row>
            <Col xl={{ size: 10, offset: 1 }}>
              <div className="page-container">
                <div className="tracker-container">
                  <div className="d-flex justify-content-between gap-m">
                    <Form onSubmit={(e) => handleSubmit('db_only', e)} className="flex-grow-1">
                      <Label for="description">
                        {t('tracker.addIngredient.search.inputLabel')}
                      </Label>
                      <div className="input-group-with-addon start">
                        <InputGroupText>
                          <img src={searchIcon} alt="search" />
                        </InputGroupText>
                        <Input
                          id="search"
                          name="description"
                          type="search"
                          enterkeyhint="search"
                          value={description}
                          onChange={(e) => setDescription(e.target.value)}
                          innerRef={searchInputRef}
                        />
                        <Button type="submit" color="primary" size="sm" className="ms-2 px-m">
                          <img src={searchButton} alt="search" />
                        </Button>
                      </div>
                    </Form>

                    <Button
                      color="link"
                      tag={Link}
                      to="scan-barcode"
                      className="p-0"
                      style={{ marginTop: '26px' }}
                    >
                      <img src={scanBarcodeIcon} width={40} height={40} alt="" />
                    </Button>
                  </div>
                </div>
                <hr className="my-0" />

                <div className="tracker-container">
                  {isAiSearching && (
                    <div className="voice-button is-submitting">
                      <div className="loader-container text-center">
                        <p className="mb-0 medium">{t('tracker.describeFood.loader.analyzing')}</p>
                      </div>
                    </div>
                  )}

                  {isSearching && !isAiSearching && (
                    <p className="text-text-variant-color text-center">
                      <Spinner size="sm" className="me-2" />
                      {t('tracker.addIngredient.loader.searching')}
                    </p>
                  )}

                  {/* render results here */}
                  {results && results.length > 0 && (
                    <>
                      <p className="small medium semi-bold mb-m">
                        {t('tracker.addIngredient.search.resultsTitle')} ({results.length}):{' '}
                      </p>
                      <ul className="tracker-ingredients-search-results-list-items">
                        {results.map((ingredient: any, index: number) => (
                          <li
                            key={ingredient.id || `ingredient_${index}`}
                            className="tracker-ingredient-item"
                          >
                            <div className="d-flex justify-content-between">
                              <div>
                                {ingredient.brand && (
                                  <span className="description text-text-variant-color">
                                    {ingredient.brand}
                                  </span>
                                )}
                                <span className="small text-text-main-color semi-bold text-truncate-2-line">
                                  {ingredient.name}
                                </span>

                                <span className="d-block description text-text-variant-color mt-xs">
                                  {ingredient.calories} kcal
                                  <span className="d-inline-block ms-3">
                                    {t('summary.macros.proteinsShort')}{' '}
                                    {(Math.round(ingredient.proteinGrams * 10) / 10)
                                      .toString()
                                      .replace('.', decimalSeparator)}
                                    g
                                  </span>
                                  <span className="d-inline-block ms-s">
                                    {t('summary.macros.fatsShort')}{' '}
                                    {(Math.round(ingredient.fatGrams * 10) / 10)
                                      .toString()
                                      .replace('.', decimalSeparator)}
                                    g
                                  </span>
                                  <span className="d-inline-block ms-s">
                                    {t('summary.macros.carbsShort')}{' '}
                                    {(Math.round(ingredient.carbsGrams * 10) / 10)
                                      .toString()
                                      .replace('.', decimalSeparator)}
                                    g
                                  </span>
                                </span>
                              </div>
                              <Button
                                color="link"
                                className="p-0"
                                onClick={() => handleAddIngredient(ingredient)}
                              >
                                <img src={plus} width={24} height={24} alt="" />
                              </Button>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </>
                  )}

                  {results && results.length === 0 && (
                    <p className="small medium semi-bold mb-m">
                      {t('tracker.addIngredient.search.noResults')}
                    </p>
                  )}

                  {results && (
                    <Button
                      type="button"
                      color="link"
                      className="search-with-ai"
                      onClick={handleSearchWithAI}
                    >
                      {t('tracker.addIngredient.search.aiOption')}
                      <img src={aiBadgeTiny} alt="" className="ms-s" />
                    </Button>
                  )}

                  {showNoFoodAlert && (
                    <Alert color="warning" className="mt-xl mb-0" fade={false}>
                      <p className="mb-0">
                        {t('tracker.describeFood.analyze.noFood.notification')}
                      </p>
                    </Alert>
                  )}
                </div>
              </div>
            </Col>
          </Row>
        </Container>

        {isAiSearching && (
          <div className="sticky-footer">
            <Container className="px-3 text-center">
              <Button type="button" color="link" onClick={handleCancel}>
                {t('tracker.describeFood.loader.cancelButton')}
              </Button>
            </Container>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default TrackerAddIngredientContainer;
